import {buildIntentsFromConfig} from '#core/utils/intent-factory.js';
import {openViewIntent} from '#core/utils/intent-utils.js';
import {ensureHierarchy, mergeTo} from '#core/utils/config-utils.js';
import {isCrawlerBot} from '#core/utils/url-utils.js';

import {ApplicationConfig} from '#core/config/application-config.js';
import {Intents} from '#core/application/intents/index.js';

import {AbstractVisualFeature} from '#core/application/modules/visual-feature.js';

import {Interface} from '../interface.js';

import {applicationHeaderFactory} from './application-header.factory';

import SCHEMA from './application-header.schema.yaml';
import PALETTE_SCHEMA from './application-header.palette-schema.yaml';

class ApplicationHeaderFeature extends AbstractVisualFeature {
    get name() {
        return 'ApplicationHeader';
    }

    get configSchema() {
        return SCHEMA;
    }

    get paletteSchema() {
        return PALETTE_SCHEMA;
    }

    getVisualConfig(PALETTE) {
        return applicationHeaderFactory(PALETTE);
    }

    beforeMojitoConfigBuild(mojitoConfig) {
        const configurations = ensureHierarchy(mojitoConfig, 'services.configurations');
        const {cookieConsent, ...config} = this.config;
        const {crossSellMenuLinks} = ApplicationConfig.config;

        // we have to filter it because mojito has a bug here, if we set empty intent they will render this button no matter what,
        // look at user-authentication-info.jsx:104 file
        let userAuthenticationButtonsList = config.userAuthenticationButtonsList.filter(buttonName => {
            return !(buttonName === 'SIGNUP_BTN' && !Intents.REGISTER);
        });
        if (!BUILT_IN_AUTHORIZATION) {
            userAuthenticationButtonsList = userAuthenticationButtonsList.filter(buttonName => {
                return !(buttonName === 'LOGIN_BTN');
            });
        }

        mergeTo(configurations, {
            CookieConsent: {
                policyLinkIntent: Intents.COOKIE_CONSENT || openViewIntent('cookies-policy'),
            },
            UserAuthenticationInfo: {
                items: [...userAuthenticationButtonsList, 'ACCOUNT_MENU'],
                signupButton: {
                    intent: Intents.REGISTER,
                },
            },
            ApplicationHeader: {
                layout: config.layout,
                cookieConsentView: {
                    hide: isCrawlerBot() || !cookieConsent.show,
                },
                [`${config.layout}Layout`]: {
                    applicationMenu: {
                        // cross-sell menu
                        showCrossSellMenu: !!(crossSellMenuLinks && crossSellMenuLinks.length > 0),
                        _mobile_showCrossSellMenu: false,
                        // search button
                        showSearch: config.showSearchButton,
                        _mobile_showSearch: config.showSearchButton,
                    },
                    mainNavigationBar: {
                        /* Important: settings menu is configured in core/application/modules/settings-menu */
                        buttonPosition: config.settingsAndHelpButtonsPosition,
                        // helpButton
                        showHelpButton: !!Intents.HELP,
                        helpIntent: Intents.HELP,
                        // session time
                        showSessionTime: config.sessionTime.show,
                        sessionTime: {
                            layoutDirection: config.sessionTime.layout,
                            showLabel: config.sessionTime.showLabel,
                        },
                        // current time
                        isCurrentTimeVisible: config.currentTime.show,
                        currentTime: {
                            isOffsetHidden: !config.currentTime.showOffset,
                        },
                    },
                },
            },
            MainNavigationBar: {
                items: buildIntentsFromConfig(ApplicationConfig.config.desktopNavigationBarItems),
            },
        });

        return super.beforeMojitoConfigBuild(mojitoConfig);
    }
}

export const ApplicationHeader = new ApplicationHeaderFeature(Interface);
